import { useState } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { useForm } from 'react-hook-form';
import { ComponentProps } from '@components/strapi/DynamicZoneComponent';
import { ContactFormSectionDTO } from '@lib/strapi/dtos/components/sections/contactFormSection.dto';
import { Prose } from '../shared-ui';
import * as gtag from '@lib/utilities/analytics';
import Turnstile from '@components/utils/turnstile';

type FormValues = { name: string; email: string; phone?: string; subject?: string; message: string };

/**
 * The contact form (D13). Field styling follows the approved system: white
 * fields, violet focus ring, RED errors — a required-field warning must read
 * as a warning, not as brand colour.
 *
 * GA4 `contact_submit` fires on success (item 13) — one of the Ads
 * conversions. Turnstile lands at P5 on the API route.
 */
const input =
  'w-full rounded-xl border-[1.5px] border-rule bg-white px-4 py-3 text-ink transition-colors hover:border-violet-400 focus:border-violet-700 focus:outline-none focus:ring-2 focus:ring-violet-050';
const err = 'mt-1 block text-sm font-bold text-error';

const ContactFormSectionImplementation = ({ section }: ComponentProps<ContactFormSectionDTO>) => {
  const { t } = useTranslation('main');
  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm<FormValues>();
  const [state, setState] = useState<'idle' | 'ok' | 'fail'>('idle');
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  const onSubmit = async (values: FormValues) => {
    const res = await fetch('/api/contact-message', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...values, turnstileToken }),
    });
    if (res.ok) {
      gtag.event('contact_submit', { event_category: 'conversion' });
      setState('ok'); reset();
    } else setState('fail');
  };

  if (state === 'ok') {
    return (
      <section className="my-10 mx-auto w-full max-w-xl rounded-2xl border border-violet-200 bg-violet-050 p-8">
        <h2 className="text-xl font-extrabold text-ink">{t('contactForm.received')}</h2>
        <p className="mt-2 text-muted">{t('contactForm.receivedNote')}</p>
      </section>
    );
  }

  return (
    // boxed card, same frame as the booking/CV forms — on the white page the
    // form needs its own surface (the old page-wide band is gone)
    <section className="my-10 mx-auto w-full max-w-xl rounded-2xl border border-rule bg-white p-6 shadow-[0_26px_60px_-20px_rgba(23,17,31,0.18)] md:p-8">
      {section?.heading ? <h2 className="text-2xl font-extrabold text-ink">{section.heading}</h2> : null}
      <Prose content={section?.intro} className="mt-2 text-muted" />
      <form onSubmit={handleSubmit(onSubmit)} className="mt-6 flex flex-col gap-4" noValidate>
        <label className="flex flex-col gap-1.5">
          <span className="font-bold text-ink">{t('contactForm.nameLabel')} *</span>
          <input className={input} {...register('name', { required: true })} />
          {errors.name ? <span className={err}>{t('contactForm.nameError')}</span> : null}
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="font-bold text-ink">{t('contactForm.emailLabel')} *</span>
          <input type="email" className={input} {...register('email', { required: true, pattern: /.+@.+\..+/ })} />
          {errors.email ? <span className={err}>{t('contactForm.emailError')}</span> : null}
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="font-bold text-ink">{t('contactForm.phoneLabel')}</span>
          <input type="tel" className={input} {...register('phone')} />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="font-bold text-ink">{t('contactForm.messageLabel')} *</span>
          <textarea rows={5} className={input} {...register('message', { required: true })} />
          {errors.message ? <span className={err}>{t('contactForm.messageError')}</span> : null}
        </label>
        <Turnstile onToken={setTurnstileToken} />
        <button type="submit" disabled={isSubmitting}
          className="self-start rounded-full bg-violet-700 px-7 py-3 font-extrabold text-white transition-colors hover:bg-violet-800 disabled:opacity-60">
          {isSubmitting ? t('contactForm.sending') : t('contactForm.submit')}
        </button>
        {state === 'fail' ? <p className={err}>{t('contactForm.failure')}</p> : null}
      </form>
    </section>
  );
};

export default ContactFormSectionImplementation;
